/**
 * Mana Locals Admin — catalog boundary schemas.
 *
 * CONTRACT SOURCES (API repository):
 *   - M66 catalog-products: `src/domains/m66-catalog-products/contracts/catalog-products.schemas.ts`
 *   - M13 catalog-offerings: `src/domains/m13-catalog-offerings/contracts/catalog-offerings.schemas.ts`
 *
 * ---------------------------------------------------------------------------
 * TWO RESOURCES, TWO AUTHORITIES — KEPT APART
 * ---------------------------------------------------------------------------
 * The catalogue and the offer are different things, and the API authorizes them
 * differently:
 *
 *   - A CANONICAL PRODUCT is the shared catalogue entry. Its read is PUBLIC scope
 *     in M66 (`permissions: []`); its WRITES are PLATFORM-scoped
 *     (`catalog.product.create`, …). Everyone signed in may browse it.
 *   - An OFFER is the tenant's own sellable row — a price, a SKU, a location. Its
 *     read is TENANT-scoped and needs `catalog.offer.read`.
 *
 * So a viewer may legitimately see a product and see NO price for it. The
 * console renders that as a fact about the actor's role, not as an error.
 */
import { z } from 'zod';

import { CollectionResponseSchema, IsoDateTimeSchema, ResponseMetaSchema, UuidSchema } from '@/api/schemas/common';

/** M66 `BrandSummarySchema` — null when the product carries no brand. */
export const BrandSummarySchema = z
  .object({
    id: UuidSchema,
    slug: z.string().min(1),
    name: z.string().min(1),
  })
  .passthrough();

export const ProductIdentifierSchema = z
  .object({
    id: UuidSchema,
    scheme: z.string().min(1),
    value: z.string().min(1),
    regionCode: z.string().nullable(),
    verification: z.enum(['unverified', 'reviewed', 'rejected']),
  })
  .passthrough();

export const CanonicalVariantSchema = z
  .object({
    id: UuidSchema,
    productId: UuidSchema,
    modelCode: z.string().nullable(),
    capacityCode: z.string().nullable(),
    colorCode: z.string().nullable(),
    regionCode: z.string().nullable(),
    versionCode: z.string().nullable(),
    axes: z.array(z.string()),
    identifiers: z.array(ProductIdentifierSchema),
    mergedIntoVariantId: UuidSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    version: z.number().int().min(1),
  })
  .passthrough();

export const PublicationStateSchema = z.enum(['draft', 'published', 'retired', 'merged']);

export const CanonicalProductSchema = z
  .object({
    id: UuidSchema,
    kind: z.enum(['product', 'service']),
    name: z.string().min(1),
    model: z.string().nullable(),
    modelCode: z.string().nullable(),
    brandId: UuidSchema.nullable(),
    brand: BrandSummarySchema.nullable(),
    identifiers: z.array(ProductIdentifierSchema),
    publicationState: PublicationStateSchema,
    variants: z.array(CanonicalVariantSchema),
    mergedIntoProductId: UuidSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    version: z.number().int().min(1),
  })
  .passthrough();

export const CanonicalProductResponseSchema = z.object({
  data: CanonicalProductSchema,
  meta: ResponseMetaSchema,
});

export const CanonicalProductListResponseSchema = CollectionResponseSchema(CanonicalProductSchema);

/**
 * M13's offer — the tenant's own sellable row.
 *
 * `title` is NULLABLE and never absent: an offering published before display
 * content existed reports an explicit null instead of the field vanishing.
 */
export const OfferSchema = z
  .object({
    id: UuidSchema,
    version: z.number().int().min(1),
    state: z.enum(['draft', 'review', 'published', 'paused', 'archived']),
    kind: z.enum(['goods', 'service', 'package', 'reservable']),
    canonicalVariantId: z.string().min(1),
    sku: z.string().min(1),
    locationId: z.string().min(1),
    price: z.object({
      amountMinor: z.number().int(),
      currency: z.string().regex(/^[A-Z]{3}$/),
    }),
    termsVersion: z.string().min(1),
    title: z.string().nullable(),
    description: z.string().nullable(),
    mediaIds: z.array(z.string()),
    attributeValues: z.record(z.string(), z.string()),
    publishedVersion: z.number().int().min(1).nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .passthrough();

export const OfferListResponseSchema = CollectionResponseSchema(OfferSchema);

export type CanonicalProduct = z.infer<typeof CanonicalProductSchema>;
export type CanonicalVariant = z.infer<typeof CanonicalVariantSchema>;
export type Offer = z.infer<typeof OfferSchema>;
export type PublicationState = z.infer<typeof PublicationStateSchema>;
